import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Cards } from "../components/Cards.tsx";
import { StatusBadge } from "../components/StatusBadge";
import Spinner from "../components/PageLoader.js";
import {
  capitalize,
  convertUTCToLocal,
  formatUKTime,
  parseDbTimestamp,
} from "../utils/functions";
import { getShift, deleteShift } from "../services/shifts.js";
import { WarningModal } from "../components/modals/WarningModal";
import { EditShiftModal } from "../components/modals/EditShiftModal.js";
import {
  EditAdministeredMedicationModal,
  isEditableMedicationId,
} from "../components/modals/EditAdministeredMedicationModal.js";
import { AddAdministeredMedicationModal } from "../components/modals/AddAdministeredMedicationModal.js";
import { DeleteIcon } from "../components/icons/DeleteIcon.jsx";
import { EditIcon } from "../components/icons/EditIcon.jsx";
import toast from "react-hot-toast";

// Safe date formatting function
const safeDateFormat = (timestamp, format) => {
  try {
    if (!timestamp) return 'N/A';

    // Handle different timestamp formats
    let processedTimestamp = timestamp;

    // If timestamp has +00:00 timezone, convert it to Z format for better compatibility
    if (typeof timestamp === 'string' && timestamp.includes('+00:00')) {
      processedTimestamp = timestamp.replace('+00:00', 'Z');
    }

    // Try direct conversion first
    let convertedTime;
    try {
      convertedTime = convertUTCToLocal(processedTimestamp);
    } catch (conversionError) {
      // Fallback: try creating a Date object directly
      const date = new Date(processedTimestamp);
      if (!isNaN(date.getTime())) {
        convertedTime = date;
      } else {
        throw new Error('Both conversion methods failed');
      }
    }

    if (!convertedTime || isNaN(new Date(convertedTime))) {
      return 'Invalid Date';
    }

    // Shift times display in UK local time on every device
    return formatUKTime(convertedTime, format);
  } catch (error) {
    return 'Invalid Date';
  }
};


export const ShiftDetails = () => {
  const [shift, setShift] = useState(null);
  const [administeredMedication, setAdministeredMedication] = useState([]);
  const [prnMedication, setPrnMedication] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  // CCS-377: which administered medication (if any) is being edited by an admin.
  const [editingMedication, setEditingMedication] = useState(null);
  // CCS-377: whether the "add administered medication" modal is open.
  const [showAddMedication, setShowAddMedication] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const urlParam = useParams();
  const navigate = useNavigate();
  const shiftId = urlParam.id;
  
  const fetchShift = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await getShift(shiftId);

      if (!response.data) {
        throw new Error('No data in response');
      }
      
      const { data } = response;

      setShift(data.Shift);
      setAdministeredMedication(data.administeredMedications || []);
      setPrnMedication(data.administeredPrns || []);
    } catch (error) {
      let errorMessage = 'Unknown error';
      if (error.response?.data) {
        if (typeof error.response.data === 'string') {
          errorMessage = error.response.data;
        } else if (error.response.data.message) {
          errorMessage = error.response.data.message;
        } else if (error.response.data.error) {
          errorMessage = error.response.data.error;
        } else {
          errorMessage = JSON.stringify(error.response.data);
        }
      } else if (error.message) {
        errorMessage = error.message;
      }

      setError(`Failed to load shift details: ${errorMessage}`);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchShift();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (loading) {
    return <Spinner />;
  }

  if (error) {
    return (
      <div className="user-profile">
        <div className="profile-details">
          <Cards classes="mb-24">
            <p>Error: {error}</p>
          </Cards>
        </div>
      </div>
    );
  }

  if (!shift) {
    return (
      <div className="user-profile">
        <div className="profile-details">
          <Cards classes="mb-24">
            <p>Shift not found</p>
          </Cards>
        </div>
      </div>
    );
  }

  let user = null;
  try {
    user = JSON.parse(localStorage.getItem("user"));
  } catch {
    // Malformed stored user — treat as non-admin, edit action stays hidden
  }
  const isAdmin = Boolean(user?.userGroup?.includes("admin"));
  const shiftEndDate = parseDbTimestamp(shift.shiftEnd || shift.endTime);
  const canEditPastShift =
    isAdmin && Boolean(shiftEndDate) && shiftEndDate < new Date();

  return (
    <div className="shift-details">
      {/* Header Section with Enhanced Design */}
      <Cards classes="mb-24 shift-details__header">
        <div className="shift-details__header-content">
          <div className="shift-details__title-section">
            <div className="shift-details__icon">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                <path d="M8 2V6H16V2H18V6H22V8H2V6H6V2H8Z" fill="currentColor"/>
                <path d="M2 10V22H22V10H2ZM4 12H20V20H4V12Z" fill="currentColor"/>
                <circle cx="8" cy="16" r="1" fill="currentColor"/>
                <circle cx="12" cy="16" r="1" fill="currentColor"/>
                <circle cx="16" cy="16" r="1" fill="currentColor"/>
              </svg>
            </div>
            <div>
              <h4 className="shift-details__title">Shift Details</h4>
              <p className="shift-details__subtitle">Shift ID: <span className="shift-details__id">{shift.shiftId}</span> • {safeDateFormat(shift.shiftStart || shift.startTime, "dd MMMM yyyy")}</p>
            </div>
          </div>
          <div className="shift-details__actions">
            {canEditPastShift && (
              <button
                className="action-btn action-btn--edit"
                onClick={() => setShowEditModal(true)}
                data-tooltip="Edit shift record"
              >
                <EditIcon />
              </button>
            )}
            <button
              className="action-btn action-btn--delete"
              onClick={() => setShowDeleteModal(true)}
              data-tooltip="Delete shift"
            >
              <DeleteIcon />
            </button>
            <div className="shift-details__status-wrapper">
              <StatusBadge status={shift.status} />
            </div>
          </div>
        </div>
      </Cards>

      {/* Main Info Grid */}
      <div className="shift-details__grid">
        {/* People Section */}
        <Cards classes="mb-24 shift-details__people">
          <div className="shift-details__section-header">
            <div className="shift-details__section-icon">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                <path d="M12 12C14.7614 12 17 9.76142 17 7C17 4.23858 14.7614 2 12 2C9.23858 2 7 4.23858 7 7C7 9.76142 9.23858 12 12 12Z" fill="var(--primary)"/>
                <path d="M12 14C7.58172 14 4 17.5817 4 22H20C20 17.5817 16.4183 14 12 14Z" fill="var(--primary)"/>
              </svg>
            </div>
            <h6>Users</h6>
          </div>
          <div className="shift-details__people-list">
            <div className="shift-details__person">
              <div className="shift-details__person-avatar">
                {shift.serviceUser.firstName.charAt(0)}{shift.serviceUser.lastName.charAt(0)}
              </div>
              <div>
                <p className="shift-details__person-name">{`${shift.serviceUser.firstName} ${shift.serviceUser.lastName}`}</p>
                <small className="shift-details__person-role">Service User</small>
              </div>
            </div>
            <div className="shift-details__person">
              <div className="shift-details__person-avatar shift-details__person-avatar--staff">
                {shift.staff.firstName.charAt(0)}{shift.staff.lastName.charAt(0)}
              </div>
              <div>
                <p className="shift-details__person-name">{`${shift.staff.firstName} ${shift.staff.lastName}`}</p>
                <small className="shift-details__person-role">Staff Member</small>
              </div>
            </div>
          </div>
        </Cards>

        {/* Shift Info Section */}
        <Cards classes="mb-24 shift-details__info">
          <div className="shift-details__section-header">
            <div className="shift-details__section-icon">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                <path d="M12 2C6.48 2 2 6.48 2 12S6.48 22 12 22 22 17.52 22 12 17.52 2 12 2ZM13 17H11V15H13V17ZM13 13H11V7H13V13Z" fill="var(--primary)"/>
              </svg>
            </div>
            <h6>Shift Information</h6>
          </div>
          <div className="shift-details__info-grid">
            <div className="shift-details__info-item">
              <label>Type</label>
              <p className="shift-details__info-value">{shift.shiftType}</p>
            </div>
            <div className="shift-details__info-item">
              <label>Call</label>
              <p className="shift-details__info-value">{capitalize(shift.call)}</p>
            </div>
            <div className="shift-details__info-item">
              <label>Start Time</label>
              <p className="shift-details__info-value shift-details__time">{safeDateFormat(shift.shiftStart || shift.startTime, "h:mm a")}</p>
            </div>
            <div className="shift-details__info-item">
              <label>End Time</label>
              <p className="shift-details__info-value shift-details__time">{safeDateFormat(shift.shiftEnd || shift.endTime, "h:mm a")}</p>
            </div>
          </div>
        </Cards>

        {/* Attendance Section */}
        <Cards classes="mb-24 shift-details__attendance">
          <div className="shift-details__section-header">
            <div className="shift-details__section-icon">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                <path d="M9 12L11 14L15 10M21 12C21 16.9706 16.9706 21 12 21C7.02944 21 3 16.9706 3 12C3 7.02944 7.02944 3 12 3C16.9706 3 21 7.02944 21 12Z" stroke="var(--success)" strokeWidth="2" fill="none"/>
              </svg>
            </div>
            <h6>Attendance</h6>
          </div>
          <div className="shift-details__attendance-grid">
            <div className="shift-details__attendance-item">
              <div className="shift-details__attendance-icon shift-details__attendance-icon--in">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                  <path d="M5 12H19M19 12L12 5M19 12L12 19" stroke="currentColor" strokeWidth="2"/>
                </svg>
              </div>
              <div>
                <label>Clocked In</label>
                <p className="shift-details__attendance-time">
                  {(shift.clockedIn || shift.clockInTime) ? safeDateFormat(shift.clockedIn || shift.clockInTime, "h:mm a") : "Not clocked in"}
                </p>
              </div>
            </div>
            <div className="shift-details__attendance-item">
              <div className="shift-details__attendance-icon shift-details__attendance-icon--out">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                  <path d="M19 12H5M5 12L12 5M5 12L12 19" stroke="currentColor" strokeWidth="2"/>
                </svg>
              </div>
              <div>
                <label>Clocked Out</label>
                <p className="shift-details__attendance-time">
                  {(shift.clockedOut || shift.clockOutTime) ? safeDateFormat(shift.clockedOut || shift.clockOutTime, "h:mm a") : "Not clocked out"}
                </p>
              </div>
            </div>
          </div>
        </Cards>

        {/* Care Notes Section */}
        {shift.careNotes?.length > 0 && (
          <Cards classes="mb-24 shift-details__notes">
            <div className="shift-details__section-header">
              <div className="shift-details__section-icon">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                  <path d="M9 5H7C5.89543 5 5 5.89543 5 7V19C5 20.1046 5.89543 21 7 21H17C18.1046 21 19 20.1046 19 19V7C19 5.89543 18.1046 5 17 5H15M9 5C9 6.10457 9.89543 7 11 7H13C14.1046 7 15 6.10457 15 5M9 5C9 3.89543 9.89543 3 11 3H13C14.1046 3 15 3.89543 15 5M12 12H15M12 16H15M9 12H9.01M9 16H9.01" stroke="var(--primary)" strokeWidth="2" fill="none"/>
                </svg>
              </div>
              <h6>Care Notes</h6>
            </div>
            <div className="shift-details__note">
              {/* care_notes is append-ordered; the last element is the latest note */}
              <p>{shift.careNotes[shift.careNotes.length - 1]?.note}</p>
            </div>
          </Cards>
        )}
      </div>

      {/* Medication Administration Section */}
      <Cards classes="mb-24 shift-details__medication">
        <div className="shift-details__section-header">
          <div className="shift-details__section-icon">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
              <path d="M19.14,12.94C19.73,12.35 19.73,11.45 19.14,10.86L13.14,4.86C12.76,4.49 12.26,4.29 11.74,4.29H5C3.9,4.29 3,5.19 3,6.29V11.74C3,12.26 3.2,12.76 3.57,13.14L9.86,19.43C10.45,20.02 11.35,20.02 11.94,19.43L19.14,12.94ZM6.5,9.79C5.4,9.79 4.5,8.89 4.5,7.79S5.4,5.79 6.5,5.79 8.5,6.69 8.5,7.79 7.6,9.79 6.5,9.79Z" fill="var(--success)"/>
            </svg>
          </div>
          <h6>Medication Administration</h6>
          {administeredMedication.length > 0 && (
            <div className="shift-details__medication-badge">
              {administeredMedication.length} administered
            </div>
          )}
          {/* CCS-377: admins can add an administered medication after the shift
              has ended (e.g. backfilling a missed record). */}
          {canEditPastShift && (
            <button
              type="button"
              className="btn btn--sm"
              style={{ marginLeft: "auto" }}
              onClick={() => setShowAddMedication(true)}
            >
              + Add medication
            </button>
          )}
        </div>

        {administeredMedication.length > 0 ? (
          <div className="shift-details__medication-content">
            <div className="shift-details__medication-group">
              <label>Medication Group</label>
              <span className="shift-details__group-name">
                {(() => {
                  return administeredMedication[0]?.medicationGroup ||
                         administeredMedication[0]?.groupName ||
                         administeredMedication[0]?.group ||
                         administeredMedication[0]?.medicationGroupName ||
                         "Morning";
                })()}
              </span>
            </div>
            
            <div className="shift-details__medication-list">
              {administeredMedication.map((admin, idx) => (
                <div key={idx} className="shift-details__medication-item">
                  <div className="shift-details__medication-header">
                    <div className="shift-details__medication-name">
                      <h6>
                        {(() => {

                          // Check multiple possible locations for medication name
                          const medicationName = admin.medication?.name ||
                                                admin.medicationName ||
                                                admin.name ||
                                                admin.medication?.medicationName;

                          const isDeleted = !admin.medication?.name && !admin.medicationName;

                          if (medicationName) {
                            return (
                              <>
                                {medicationName}
                                {isDeleted && (
                                  <span
                                    style={{
                                      marginLeft: '8px',
                                      fontSize: '12px',
                                      color: 'var(--status-warning)',
                                      backgroundColor: 'var(--status-warning-bg, #fff3cd)',
                                      padding: '2px 6px',
                                      borderRadius: '4px',
                                      border: '1px solid var(--status-warning)',
                                      fontWeight: 'normal'
                                    }}
                                    title="This medication has been deleted but administration record is preserved for audit purposes"
                                  >
                                    Deleted
                                  </span>
                                )}
                              </>
                            );
                          }

                          // Only show as truly unknown if no name found anywhere
                          return (
                            <>
                              [Unknown Medication]
                              <span
                                style={{
                                  marginLeft: '8px',
                                  fontSize: '12px',
                                  color: 'var(--status-warning)',
                                  backgroundColor: 'var(--status-warning-bg, #fff3cd)',
                                  padding: '2px 6px',
                                  borderRadius: '4px',
                                  border: '1px solid var(--status-warning)',
                                  fontWeight: 'normal'
                                }}
                                title="This medication has been deleted but administration record is preserved for audit purposes"
                              >
                                Deleted
                              </span>
                            </>
                          );
                        })()}
                      </h6>
                      <small>Form: {admin.medication?.form || admin.form || admin.medicationForm || admin.medication?.medicationForm || 'Unknown'}</small>
                    </div>
                    <div className="shift-details__medication-status">
                      <span className="shift-details__status-badge shift-details__status-badge--administered">
                        Administered
                      </span>
                      {/* CCS-377: admins can correct this record after the shift
                          has ended. A record for a deleted medication has no id
                          to update against, so the action is hidden for it. */}
                      {canEditPastShift && isEditableMedicationId(admin.medication?.id || admin.medicationId) && (
                        <button
                          className="action-btn action-btn--edit"
                          onClick={() => setEditingMedication(admin)}
                          data-tooltip="Edit medication record"
                          aria-label="Edit medication record"
                        >
                          <EditIcon />
                        </button>
                      )}
                    </div>
                  </div>

                  <div className="shift-details__medication-details">
                    <div className="shift-details__medication-detail">
                      <label>Dose & Quantity</label>
                      <p>
                        {(() => {
                          const dose = admin.medication?.dose || admin.dose || admin.medicationDose || admin.medication?.dosage;
                          const quantity = admin.medication?.quantity || admin.quantity || admin.medicationQuantity || admin.medication?.amount;
                          return `${dose || 'Unknown'} • ${quantity || 'Unknown'}`;
                        })()}
                      </p>
                    </div>
                    <div className="shift-details__medication-detail">
                      <label>Route</label>
                      <p>
                        {(() => {
                          const route = admin.medication?.route || admin.route || admin.medicationRoute || admin.medication?.administrationRoute;
                          return route ? capitalize(route) : 'Unknown';
                        })()}
                      </p>
                    </div>
                    <div className="shift-details__medication-detail">
                      <label>Instructions</label>
                      <p>
                        {(() => {
                          const instructions = admin.medication?.instructions || admin.instructions || admin.medicationInstructions || admin.medication?.directions || admin.medication?.notes;
                          return instructions || 'No instructions available';
                        })()}
                      </p>
                    </div>
                  </div>
                  
                  <div className="shift-details__administration-info">
                    <div className="shift-details__admin-header">
                      <div className="shift-details__admin-by">
                        <div className="shift-details__admin-avatar">
                          {admin.administrationDetails.signature?.charAt(0) || "A"}
                        </div>
                        <div>
                          <p>{admin.administrationDetails.signature}</p>
                          <small>{admin.administrationDetails.administrationType}</small>
                        </div>
                      </div>
                      <div className="shift-details__admin-time">
                        {safeDateFormat(admin.administrationDetails?.timestamp, "HH:mm")}
                      </div>
                    </div>
                    {admin.administrationDetails.notes && (
                      <div className="shift-details__admin-notes">
                        <label>Notes</label>
                        <p>{admin.administrationDetails.notes}</p>
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        ) : (
          <div className="shift-details__empty">
            <div className="shift-details__empty-icon">
              <svg width="32" height="32" viewBox="0 0 24 24" fill="none">
                <path d="M12 2C6.48 2 2 6.48 2 12S6.48 22 12 22 22 17.52 22 12 17.52 2 12 2ZM13 17H11V15H13V17ZM13 13H11V7H13V13Z" fill="var(--gray)"/>
              </svg>
            </div>
            <h6>No Medications Administered</h6>
            <p>No medications have been administered during this shift yet.</p>
          </div>
        )}
      </Cards>

      {/* PRN Administration Section */}
      <Cards classes="mb-24 shift-details__prn">
        <div className="shift-details__section-header">
          <div className="shift-details__section-icon">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
              <path d="M12 2L13.09 8.26L22 9L13.09 9.74L12 16L10.91 9.74L2 9L10.91 8.26L12 2Z" fill="var(--pending)"/>
            </svg>
          </div>
          <h6>PRN Medications</h6>
          {prnMedication.length > 0 && (
            <div className="shift-details__prn-badge">
              {prnMedication.length} administered
            </div>
          )}
        </div>

        {prnMedication.length > 0 ? (
          <div className="shift-details__prn-list">
            {prnMedication.map((admin, idx) => (
              <div key={idx} className="shift-details__medication-item">
                <div className="shift-details__medication-header">
                  <div className="shift-details__medication-name">
                    <h6>
                      {admin.medication?.name ||
                       admin.medicationName ||
                       `[Deleted PRN ID: ${admin.medication?.id || admin.medicationId || 'Unknown'}]`}
                      {(!admin.medication?.name && !admin.medicationName) && (
                        <span
                          style={{
                            marginLeft: '8px',
                            fontSize: '12px',
                            color: 'var(--status-warning)',
                            backgroundColor: 'var(--status-warning-bg, #fff3cd)',
                            padding: '2px 6px',
                            borderRadius: '4px',
                            border: '1px solid var(--status-warning)',
                            fontWeight: 'normal'
                          }}
                          title="This PRN medication has been deleted but administration record is preserved for audit purposes"
                        >
                          Deleted
                        </span>
                      )}
                    </h6>
                    <small>Form: {admin.medication?.form || admin.medicationForm || admin.form || 'Unknown'}</small>
                  </div>
                  <div className="shift-details__medication-status">
                    <span className="shift-details__status-badge shift-details__status-badge--prn">
                      PRN Given
                    </span>
                  </div>
                </div>
                
                <div className="shift-details__medication-details">
                  <div className="shift-details__medication-detail">
                    <label>Dose & Quantity</label>
                    <p>
                      {admin.medication?.dose || admin.medicationDose || admin.dose || 'Unknown'} • {admin.medication?.quantity || admin.medicationQuantity || admin.quantity || 'Unknown'}
                    </p>
                  </div>
                  <div className="shift-details__medication-detail">
                    <label>Route</label>
                    <p>
                      {(() => {
                        const route = admin.medication?.route || admin.medicationRoute || admin.route;
                        return route ? capitalize(route) : 'Unknown';
                      })()}
                    </p>
                  </div>
                  <div className="shift-details__medication-detail">
                    <label>Instructions</label>
                    <p>{admin.medication?.instructions || admin.medicationInstructions || admin.instructions || 'No instructions available'}</p>
                  </div>
                </div>
                
                <div className="shift-details__administration-info">
                  <div className="shift-details__admin-header">
                    <div className="shift-details__admin-by">
                      <div className="shift-details__admin-avatar">
                        {admin.administrationDetails.signature?.charAt(0) || "A"}
                      </div>
                      <div>
                        <p>{admin.administrationDetails.signature}</p>
                        <small>{admin.administrationDetails.administrationType}</small>
                      </div>
                    </div>
                    <div className="shift-details__admin-time">
                      {safeDateFormat(admin.administrationDetails?.timestamp, "HH:mm")}
                    </div>
                  </div>
                  {admin.administrationDetails.notes && (
                    <div className="shift-details__admin-notes">
                      <label>Notes</label>
                      <p>{admin.administrationDetails.notes}</p>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="shift-details__empty">
            <div className="shift-details__empty-icon">
              <svg width="32" height="32" viewBox="0 0 24 24" fill="none">
                <path d="M12 2C6.48 2 2 6.48 2 12S6.48 22 12 22 22 17.52 22 12 17.52 2 12 2ZM13 17H11V15H13V17ZM13 13H11V7H13V13Z" fill="var(--gray)"/>
              </svg>
            </div>
            <h6>No PRN Medications</h6>
            <p>No PRN medications have been administered during this shift.</p>
          </div>
        )}
      </Cards>

      {/* Edit Shift Modal (admin, elapsed shifts) */}
      {showEditModal && (
        <EditShiftModal
          isOpen={showEditModal}
          onClose={() => setShowEditModal(false)}
          shift={shift}
          onSaved={fetchShift}
        />
      )}

      {/* Edit Administered Medication Modal (CCS-377: admin, elapsed shifts) */}
      {editingMedication && (
        <EditAdministeredMedicationModal
          isOpen={Boolean(editingMedication)}
          onClose={() => setEditingMedication(null)}
          shiftId={shift.shiftId}
          serviceUserId={shift.serviceUser?.id}
          administration={editingMedication}
          onSaved={fetchShift}
        />
      )}

      {/* Add Administered Medication Modal (CCS-377: admin, elapsed shifts) */}
      {showAddMedication && (
        <AddAdministeredMedicationModal
          isOpen={showAddMedication}
          onClose={() => setShowAddMedication(false)}
          shiftId={shift.shiftId}
          serviceUserId={shift.serviceUser?.id}
          existingMedicationIds={administeredMedication
            .map((a) => a.medication?.id || a.medicationId)
            .filter(Boolean)}
          onSaved={fetchShift}
        />
      )}

      {/* Delete Confirmation Modal */}
      <WarningModal
        isOpen={showDeleteModal}
        onCancel={() => setShowDeleteModal(false)}
        title="Delete Shift"
        message={`Are you sure you want to delete shift #${shift?.shiftId}? This action cannot be undone and will permanently remove all shift data including administered medications.`}
        onConfirm={handleDeleteShift}
        isLoading={isDeleting}
      />
    </div>
  );

  async function handleDeleteShift() {
    setIsDeleting(true);
    try {
      await deleteShift(shiftId);
      toast.success("Shift deleted successfully");
      navigate("/rotas");
    } catch (error) {
      toast.error(error.message || "Failed to delete shift");
    } finally {
      setIsDeleting(false);
      setShowDeleteModal(false);
    }
  }
};
